import * as Api from '@ViewModels';
import {
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '@WebComponents/autocomplete/SimpleAutoCompleteSearchField';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../models/NameUtils';
import { areOwnershipFiltersEqual, getUserFromOwnershipFilter } from '../../../models/UiUtils';
import { useUserQuery } from '../../../queries';
import { DeprecatedSelect, ISelectOption } from '../DeprecatedSelect';
import { styleSheet } from './styles';

const ownedByTitle = 'Contacts owned by...';

export interface IOwnershipFilterDropdownProps {
	allowSelectAUserOption?: boolean;
	disableUserSearch?: boolean;
	selectedUserId?: string;
	initialOwnershipFilter: Api.IOwnershipFilter | null;
	onOwnershipFilterChanged: (option: Api.IOwnershipFilter) => void;
	onSelectedUserChanged?: (value: string) => void;
	className?: string;
	styles?: StyleDeclarationValue[];
	disabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	filterOptions?: Api.IOwnershipFilter[];
	showOwnedByOption?: boolean;
	ownedByFilter?: Api.IOwnershipFilter;
}

export const OwnershipFilterDropdown = ({
	allowSelectAUserOption,
	disableUserSearch,
	selectedUserId,
	initialOwnershipFilter,
	onOwnershipFilterChanged,
	onSelectedUserChanged,
	className,
	styles,
	disabled,
	impersonationContext,
	filterOptions,
	showOwnedByOption,
	ownedByFilter,
}: IOwnershipFilterDropdownProps) => {
	const userSession = useUserSession();

	const selectedUser = getUserFromOwnershipFilter(initialOwnershipFilter) ?? selectedUserId;
	const userQuery = useUserQuery({
		enabled: Boolean(selectedUser),
		id: selectedUser ?? '',
		impersonationContext,
	});
	const onRenderOwnershipOptionPlaceholder = React.useCallback(() => 'Please select...', []);

	// State to track if user has interacted with the search field
	const [hasUserInteracted, setHasUserInteracted] = React.useState(false);

	// Ref for focusing the search field
	const searchFieldInputRef = React.useRef<HTMLInputElement>(null);

	const options = React.useMemo(() => {
		if (filterOptions) {
			return filterOptions.map(x => {
				return { id: `filter-self-${x.criteria.property}-${x.criteria.value}`, dataContext: x, text: x.title };
			});
		}
		let value =
			userSession.user.ownershipFilters?.map(x => {
				return { id: `filter-${x.criteria.property}-${x.criteria.value}`, dataContext: x, text: x.title };
			}) ?? [];

		if (ownedByFilter) {
			// if sending on behalf of an employee, we want to allow easy filtering by contacts they own.
			// otherwise we should revert back to the normal 'Contacts owned by' option that makes you pick a user.
			value.push({
				id: `filter-owned-by`,
				dataContext: ownedByFilter,
				text: ownedByFilter.title,
			});
		} else {
			const customOwnedById = selectedUser && selectedUser !== userSession.user.id ? selectedUser : undefined;
			const defaultOwnedByFilter = {
				title: ownedByTitle,
				criteria: { property: Api.ContactFilterCriteriaProperty.OwnedBy, value: customOwnedById },
			};

			if (showOwnedByOption) {
				value.push({
					id: `filter-owned-by`,
					dataContext: defaultOwnedByFilter,
					text: ownedByTitle,
				});
			}
		}

		if (impersonationContext) {
			value = value.filter(option => {
				// Filter out 'My Connections' since impersonation context is valid
				if (
					option.dataContext.criteria.property === Api.ContactFilterCriteriaProperty.Connections ||
					(option.dataContext.criteria.property === Api.ContactFilterCriteriaProperty.OwnedBy &&
						option.dataContext.criteria.value === userSession.user.id)
				) {
					return false;
				}
				return true;
			});
		}
		return value;
	}, [selectedUser, userSession.user, impersonationContext, filterOptions, showOwnedByOption, ownedByFilter]);

	const [selectedOption, setSelectedOption] = React.useState<ISelectOption<Api.IOwnershipFilter>>(
		options.find(x => areOwnershipFiltersEqual(userSession.user.id, x.dataContext, initialOwnershipFilter))
	);

	React.useEffect(() => {
		// We don't have a good way of differentiating between the 'Contacts I own' and 'Contacts owned by' filters.
		// Both use the same filter property and might have the same filter value.
		// So if the initial filter is for 'Contacts owned by' don't try to search through the options
		// as it will return 'Contacts I own'
		if (initialOwnershipFilter?.title !== ownedByTitle) {
			setSelectedOption(
				options.find(x => areOwnershipFiltersEqual(userSession.user.id, x.dataContext, initialOwnershipFilter))
			);
		}
	}, [initialOwnershipFilter, userSession.user.id, options]);

	const [showUserSearch, setShowUserSearch] = React.useState(
		initialOwnershipFilter?.criteria?.property === Api.ContactFilterCriteriaProperty.OwnedBy &&
			initialOwnershipFilter.criteria.value !== userSession.user.id
	);

	// Focus the search field when showUserSearch becomes true
	React.useEffect(() => {
		if (selectedUser) return;
		if (showUserSearch && searchFieldInputRef.current) {
			// Small delay to ensure the component is fully rendered
			setTimeout(() => {
				searchFieldInputRef.current?.focus();
			}, 100);
		}
	}, [showUserSearch, selectedUser]);

	// Reset interaction state when showUserSearch changes
	React.useEffect(() => {
		setHasUserInteracted(false);
	}, [showUserSearch]);

	const onOwnershipOptionChanged = (value: ISelectOption<Api.IOwnershipFilter>) => {
		setSelectedOption(value);
		setShowUserSearch(
			value.dataContext.criteria?.property === Api.ContactFilterCriteriaProperty.OwnedBy &&
				value.dataContext.criteria.value !== userSession.user.id
		);
		onOwnershipFilterChanged(value.dataContext);
	};

	const selectedUserChanged = (value: ISimpleAutoCompleteSearchFieldItemSelectionEvent<Api.UserViewModel>) => {
		if (onSelectedUserChanged) {
			onSelectedUserChanged(value.selection.id);
		}
	};

	const handleSearchFieldBlur = () => {
		setHasUserInteracted(true);
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<DeprecatedSelect
				disabled={disabled}
				onOptionClick={onOwnershipOptionChanged}
				onRenderPlaceholder={onRenderOwnershipOptionPlaceholder}
				options={options}
				selectedOption={selectedOption}
				styles={[styleSheet.dropdown]}
				triggerStyles={[disabled && styleSheet.dropdownDisabled]}
			/>

			{allowSelectAUserOption && showUserSearch ? (
				<>
					<h6 className={css(styleSheet.label)}>Owner</h6>
					<SimpleAutoCompleteSearchField
						key={userQuery.data?.id && !userQuery.isPending ? userQuery.data?.id : 'searching'}
						disabled={disabled || disableUserSearch}
						// @ts-ignore
						onItemSelected={selectedUserChanged}
						onBlur={handleSearchFieldBlur}
						onInputRef={searchFieldInputRef}
						placeholder='Search name'
						type={Api.ResourceAutoCompleteViewModelType.User}
						initialSearchQuery={userQuery.data ? getDisplayName(userQuery.data) : ''}
					/>
					{!selectedUser && hasUserInteracted ? (
						<p role='alert' className={css(styleSheet.formErrorLine)}>
							Owner is required when selecting Contacts owned by
						</p>
					) : null}
				</>
			) : null}
		</div>
	);
};
